/**
 * Hook useCalendarOAuthDiagnostics
 *
 * Busca eventos de auditoria do fluxo OAuth do Google Calendar
 * (RPC `get_calendar_oauth_diagnostics`). Permite filtrar por falhas
 * e por correlation_id para inspeção pontual de uma tentativa.
 *
 * RELEASE:
 *   2026-06-24 v1.0.0 - Versão inicial. Auto-refresh quando enabled.
 */

import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

export interface CalendarOAuthEvent {
  id: string;
  tenant_id: string | null;
  correlation_id: string;
  event_type: string;
  event_source: string;
  phase: string;
  provider_slug: string;
  provider_email: string | null;
  actor_user_id: string | null;
  actor_full_name: string | null;
  actor_role: string | null;
  integration_id: string | null;
  http_status: number | null;
  latency_ms: number | null;
  google_error_code: string | null;
  google_error_description: string | null;
  metadata: Record<string, unknown> | null;
  raw_response_snapshot: Record<string, unknown> | null;
  change_diff: Record<string, unknown> | null;
  processing_status: 'pending' | 'success' | 'failed' | 'skipped';
  error_message: string | null;
  error_stack: string | null;
  created_at: string;
  completed_at: string | null;
}

interface UseDiagnosticsOptions {
  enabled?: boolean;
  onlyFailed?: boolean;
  correlationId?: string | null;
  limit?: number;
  /** Polling em ms (0 = desabilitado) */
  refetchInterval?: number;
}

export function useCalendarOAuthDiagnostics(options: UseDiagnosticsOptions = {}) {
  const {
    enabled = true,
    onlyFailed = false,
    correlationId = null,
    limit = 100,
    refetchInterval = 5000,
  } = options;

  return useQuery({
    queryKey: ['calendar-oauth-diagnostics', { onlyFailed, correlationId, limit }],
    enabled,
    refetchInterval: enabled ? refetchInterval : false,
    queryFn: async () => {
      const { data, error } = await supabase.rpc('get_calendar_oauth_diagnostics', {
        p_tenant_hint: null,
        p_limit: limit,
        p_only_failed: onlyFailed,
        p_correlation_id: correlationId,
      });
      if (error) throw error;
      return (data ?? []) as CalendarOAuthEvent[];
    },
  });
}

/**
 * Agrupa eventos por correlation_id em ordem cronológica.
 */
export interface CalendarOAuthTimeline {
  correlation_id: string;
  started_at: string;
  ended_at: string;
  status: 'pending' | 'success' | 'failed' | 'skipped';
  total_latency_ms: number;
  events: CalendarOAuthEvent[];
}

export function groupByCorrelation(events: CalendarOAuthEvent[]): CalendarOAuthTimeline[] {
  const map = new Map<string, CalendarOAuthEvent[]>();
  for (const ev of events) {
    if (!map.has(ev.correlation_id)) map.set(ev.correlation_id, []);
    map.get(ev.correlation_id)!.push(ev);
  }
  const groups: CalendarOAuthTimeline[] = [];
  for (const [cid, evs] of map.entries()) {
    // Ordena ascendente para a timeline
    const sorted = [...evs].sort(
      (a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime(),
    );
    const lastFailed = sorted.find((e) => e.processing_status === 'failed');
    const lastDone = sorted.find((e) => e.event_type === 'oauth.flow.completed');
    const status: CalendarOAuthTimeline['status'] = lastDone
      ? 'success'
      : lastFailed
        ? 'failed'
        : (sorted[sorted.length - 1]?.processing_status ?? 'pending');
    const startedAt = sorted[0].created_at;
    const endedAt = sorted[sorted.length - 1].created_at;
    const totalLatency =
      new Date(endedAt).getTime() - new Date(startedAt).getTime();
    groups.push({
      correlation_id: cid,
      started_at: startedAt,
      ended_at: endedAt,
      status,
      total_latency_ms: totalLatency,
      events: sorted,
    });
  }
  // Ordena grupos por data desc (mais recente primeiro)
  return groups.sort(
    (a, b) => new Date(b.started_at).getTime() - new Date(a.started_at).getTime(),
  );
}
